import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { api, refreshAccessToken } from "@/lib/api/client";
import { API_ORIGIN } from "@/lib/config";
import { getAccessToken } from "@/lib/auth/tokens";
import { unwrap } from "@/lib/api/unwrap";
import type { components } from "@/lib/api/schema.gen";

import { reportKeys } from "./keys";
import type {
  AssessmentDataRow,
  AssessmentFilterState,
  AssessmentLevel,
  AssessmentSummary,
  EnterpriseFilterState,
  EnterpriseProgressRow,
  EnterpriseReportDetail,
  EnterpriseReportRow,
  FilterOptions,
  ReportFacets,
  ReportPage,
} from "./types";

/** DRF paginated envelope. */
interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Serialize multi-value params as repeated keys (`?sectors=a&sectors=b`), which
 * is what DRF's `getlist` / django-filter `ModelMultipleChoiceFilter` expect.
 */
const arrayQuerySerializer = {
  array: { style: "form" as const, explode: true },
};

/** Drop empty strings / empty arrays so blank controls don't hit the API. */
function clean(query: object): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value == null || value === "") continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[key] = value;
  }
  return out;
}

// openapi-fetch types each endpoint's query params exactly; the report filters
// are a loose, endpoint-agnostic bag (single value serving a multi-value param,
// etc.), so route GETs through one deliberately-loosened helper and keep the
// response strongly typed via the generic. The path strings are still the real
// generated routes.
type RawResult = Promise<{
  data?: unknown;
  error?: unknown;
  response: Response;
}>;
const rawGet = api.GET as unknown as (path: string, opts: unknown) => RawResult;

function apiGet<T>(path: string, query?: object): Promise<T> {
  return unwrap(
    rawGet(path, {
      params: { query: clean(query ?? {}) },
      querySerializer: arrayQuerySerializer,
    }),
  ) as Promise<T>;
}

// --- filter options + cascade ------------------------------------------------

export function useFilterOptions() {
  return useQuery({
    queryKey: reportKeys.options(),
    staleTime: 5 * 60 * 1000, // definition data — rarely changes within a session
    queryFn: () => apiGet<FilterOptions>("/api/v1/filters/options/"),
  });
}

/**
 * The options each filter of a report page can still offer, given the others
 * (self_diagnostic.report_facets, shared with the Django pages). Keyed on the
 * filter state, keeping the previous lists on screen while the next arrive so
 * the dropdowns don't empty out between keystrokes.
 */
export function useReportFacets(page: ReportPage, filters: object) {
  return useQuery({
    queryKey: reportKeys.facets(page, filters),
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiGet<ReportFacets>("/api/v1/filters/facets/", { page, ...filters }),
  });
}

// --- enterprise list / detail / progress ------------------------------------

export function useEnterpriseList(filters: EnterpriseFilterState, page: number) {
  return useQuery({
    queryKey: reportKeys.enterprises(filters, page),
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiGet<Paginated<EnterpriseReportRow>>("/api/v1/reports/enterprises/", {
        ...filters,
        page,
      }),
  });
}

export function useEnterpriseDetail(id: string | null) {
  return useQuery({
    queryKey: reportKeys.enterprise(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/reports/enterprises/{id}/", {
          params: { path: { id: id as string } },
        }),
      ) as unknown as Promise<EnterpriseReportDetail>,
  });
}

export function useEnterpriseProgress(
  filters: EnterpriseFilterState,
  page: number,
) {
  return useQuery({
    queryKey: reportKeys.progress(filters, page),
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiGet<Paginated<EnterpriseProgressRow>>(
        "/api/v1/reports/enterprises/progress/",
        { ...filters, page },
      ),
  });
}

// --- customizable assessment -------------------------------------------------

export function useAssessmentSummary(filters: AssessmentFilterState) {
  return useQuery({
    queryKey: reportKeys.assessmentSummary(filters),
    queryFn: () =>
      apiGet<AssessmentSummary>("/api/v1/reports/assessment/", { ...filters }),
  });
}

export function useAssessmentData(
  filters: AssessmentFilterState,
  level: AssessmentLevel,
  parentId: string | null,
) {
  return useQuery({
    queryKey: reportKeys.assessmentData(filters, level, parentId),
    // The three nested levels require a parent to drill into.
    enabled: level === "thematic" || Boolean(parentId),
    queryFn: () =>
      apiGet<AssessmentDataRow[]>("/api/v1/reports/assessment/data/", {
        ...filters,
        type: level,
        parent_id: parentId ?? undefined,
      }),
  });
}

/**
 * Enterprises inside one breakdown bucket (sector / partner group / progress
 * step) of the filtered set — fetched lazily when a row is expanded, so the
 * reviewer can see *which* enterprises (parity with the legacy accordions).
 */
export function useGroupEnterprises(
  group: "sector" | "partner_group" | "step",
  key: string,
  filters: AssessmentFilterState,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["reports", "assessment-group", group, key, filters],
    enabled: enabled && Boolean(key),
    queryFn: () =>
      apiGet<components["schemas"]["AssessmentGroupEnterprise"][]>(
        "/api/v1/reports/assessment/enterprises/",
        { ...filters, group, key },
      ),
  });
}

// --- CSV exports (server-generated) ------------------------------------------

/**
 * Trigger a server-generated CSV download. The CSV is built entirely by the
 * API; the browser just fetches it (with the JWT) and saves the blob. Filters
 * are forwarded so the export matches what's on screen.
 *
 * This can't go through the openapi-fetch client (it needs a blob body, not the
 * typed JSON), so it re-implements that client's refresh-on-401: if the (15-min)
 * access token has expired it refreshes once and retries, matching every other
 * request in the app rather than failing an otherwise-valid session.
 */
export async function downloadCsv(
  path: string,
  query: object,
  filename: string,
): Promise<void> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(clean(query))) {
    if (Array.isArray(value)) value.forEach((v) => params.append(key, String(v)));
    else params.append(key, String(value));
  }
  const qs = params.toString();
  const requestUrl = `${API_ORIGIN}${path}${qs ? `?${qs}` : ""}`;

  const request = (token: string | null) =>
    fetch(requestUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

  let response = await request(getAccessToken());
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) response = await request(refreshed);
  }
  if (!response.ok) {
    throw new Error(`Export failed (${response.status})`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
